import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Loader from 'components/Loader';
import Filter from 'components/Filter';
import Products from 'components/Products';
import Cart from 'components/Cart';

import { useI18n } from 'contexts/i18n-context';
import { useProducts } from 'contexts/products-context';
import { useAuth } from 'contexts/auth-context';
import { useCart } from 'contexts/cart-context';
import { ICartProduct, IProduct, IRestaurantOrder, IRestaurantOrderItem } from 'models';
import {
  getFavouriteProducts,
  setProductFavourite,
} from 'services/products';
import { getRestaurantOrders } from 'services/orders';

import logo from 'assets/jiahe-logo.png';

import * as S from './style';

type AccountView = 'catalogue' | 'favourites' | 'orders';

function App() {
  const { isFetching, products, fetchProducts, filters } = useProducts();
  const { auth, isAuthenticated, isRestaurant, login, logout } = useAuth();
  const { addProducts, openCart } = useCart();
  const { locale, setLocale, t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const [accountView, setAccountView] = useState<AccountView>('catalogue');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [favouriteProducts, setFavouriteProducts] = useState<IProduct[]>([]);
  const [orders, setOrders] = useState<IRestaurantOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<IRestaurantOrder | null>(
    null
  );
  const [isAccountLoading, setIsAccountLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const refreshFavourites = useCallback(async () => {
    if (!auth?.accessToken) {
      setFavouriteProducts([]);
      return;
    }

    const nextFavouriteProducts = await getFavouriteProducts(auth.accessToken);
    setFavouriteProducts(
      nextFavouriteProducts.map((product) => ({
        ...product,
        isFavourite: true,
      }))
    );
  }, [auth?.accessToken]);

  const refreshOrders = useCallback(async () => {
    if (!auth?.accessToken || !isRestaurant) {
      setOrders([]);
      return;
    }

    setOrders(await getRestaurantOrders(auth.accessToken));
  }, [auth?.accessToken, isRestaurant]);

  useEffect(() => {
    refreshFavourites().catch(() => setFavouriteProducts([]));
  }, [refreshFavourites]);

  useEffect(() => {
    if (accountView !== 'orders') {
      setSelectedOrder(null);
      return;
    }

    setIsAccountLoading(true);
    refreshOrders()
      .catch(() => setOrders([]))
      .finally(() => setIsAccountLoading(false));
  }, [accountView, refreshOrders]);

  const handleSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    setAuthError('');

    try {
      await login(username, password);
      setPassword('');
      setAccountView('catalogue');
    } catch (error) {
      setAuthError(t('account.signInFailed'));
    }
  };

  const handleLogout = () => {
    logout();
    setAccountView('catalogue');
    setFavouriteProducts([]);
    setOrders([]);
  };

  const favouriteProductIds = useMemo(
    () => new Set(favouriteProducts.map((product) => product.id)),
    [favouriteProducts]
  );

  const catalogueProducts = useMemo(
    () =>
      products.map((product) => ({
        ...product,
        isFavourite: favouriteProductIds.has(product.id),
      })),
    [favouriteProductIds, products]
  );

  const catalogueProductById = useMemo(
    () =>
      catalogueProducts.reduce<Record<number, IProduct>>((result, product) => {
        result[product.id] = product;
        return result;
      }, {}),
    [catalogueProducts]
  );

  const accountProducts =
    accountView === 'favourites' ? favouriteProducts : catalogueProducts;
  const isOrderHistoryView = accountView === 'orders';

  const categoryFilteredProducts = useMemo(() => {
    const activeCategoryId = filters[0];

    if (!activeCategoryId) {
      return accountProducts;
    }

    return accountProducts.filter(
      (product) =>
        String(product.categoryId ?? product.categoryName) === activeCategoryId
    );
  }, [accountProducts, filters]);

  const visibleProducts = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return categoryFilteredProducts;
    }

    return categoryFilteredProducts.filter((product) =>
      [
        product.title,
        product.description,
        product.categoryName,
        product.categoryNameEn,
        product.categoryNameZh,
        product.packageSpec,
        product.unit,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearchTerm)
        )
    );
  }, [categoryFilteredProducts, searchTerm]);

  const handleToggleFavourite = async (product: IProduct) => {
    if (!auth?.accessToken) {
      return;
    }

    const nextIsFavourite = !product.isFavourite;
    await setProductFavourite(product.id, nextIsFavourite, auth.accessToken);
    await refreshFavourites();
  };

  const createCartProductFromOrderItem = (
    item: IRestaurantOrderItem
  ): ICartProduct => ({
    id: item.product,
    sku: null,
    title:
      locale === 'en'
        ? item.name_en_snapshot || item.name_zh_snapshot
        : item.name_zh_snapshot || item.name_en_snapshot,
    description: '',
    availableSizes: [],
    style: '',
    price: item.price_snapshot === null ? null : Number(item.price_snapshot),
    installments: 0,
    currencyId: 'EUR',
    currencyFormat: '€',
    isFreeShipping: false,
    packageSpec: item.package_spec_snapshot,
    unit: item.unit_snapshot,
    quantity: item.quantity,
  });

  const handleReorder = (order: IRestaurantOrder) => {
    addProducts(order.items.map(createCartProductFromOrderItem));
    openCart();
  };

  const getOrderItemName = useCallback(
    (item: IRestaurantOrderItem) =>
      locale === 'en'
        ? item.name_en_snapshot || item.name_zh_snapshot
        : item.name_zh_snapshot || item.name_en_snapshot,
    [locale]
  );

  const getOrderItemCategoryLabel = useCallback(
    (item: IRestaurantOrderItem) => {
      const product = catalogueProductById[item.product];

      if (!product) {
        return t('account.unknownCategory');
      }

      const categoryNames = [
        product.categoryNameZh || product.categoryName,
        product.categoryNameEn,
      ].filter(Boolean);

      return categoryNames.length
        ? Array.from(new Set(categoryNames)).join(' / ')
        : t('account.unknownCategory');
    },
    [catalogueProductById, t]
  );

  const groupedSelectedOrderItems = useMemo(() => {
    if (!selectedOrder) {
      return [];
    }

    return selectedOrder.items.reduce<
      {
        category: string;
        items: { index: number; item: IRestaurantOrderItem }[];
      }[]
    >((result, item, index) => {
      const category = getOrderItemCategoryLabel(item);
      const existingGroup = result.find((group) => group.category === category);
      const indexedItem = { index: index + 1, item };

      if (existingGroup) {
        existingGroup.items.push(indexedItem);
        return result;
      }

      return [...result, { category, items: [indexedItem] }];
    }, []);
  }, [getOrderItemCategoryLabel, selectedOrder]);

  const handleOrderSuccess = () => {
    if (isAuthenticated && isRestaurant) {
      setSelectedOrder(null);
      setAccountView('orders');
      setIsAccountLoading(true);
      refreshOrders()
        .catch(() => setOrders([]))
        .finally(() => setIsAccountLoading(false));
      return;
    }

    setAccountView('catalogue');
  };

  return (
    <S.Container>
      {isFetching && <Loader />}
      <S.HeaderBanner>
        <S.HeaderContent>
          <S.Logo src={logo} alt={t('brand.name')} />
        </S.HeaderContent>
      </S.HeaderBanner>
      <S.LanguageBar>
        <S.LanguageGroup aria-label="Language">
          <S.LanguageButton
            type="button"
            onClick={() => setLocale('en')}
            aria-label="EN"
            aria-pressed={locale === 'en'}
            isSelected={locale === 'en'}
          >
            EN
          </S.LanguageButton>
          <S.LanguageButton
            type="button"
            onClick={() => setLocale('zh')}
            aria-label="中文"
            aria-pressed={locale === 'zh'}
            isSelected={locale === 'zh'}
          >
            中文
          </S.LanguageButton>
        </S.LanguageGroup>
      </S.LanguageBar>
      <S.AccountBar>
        {!isAuthenticated ? (
          <S.LoginForm onSubmit={handleLogin}>
            <S.LoginField>
              <S.LoginLabel htmlFor="account-username">
                {t('account.username')}
              </S.LoginLabel>
              <S.LoginInput
                id="account-username"
                aria-label={t('account.username')}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder={t('account.username')}
              />
            </S.LoginField>
            <S.LoginField>
              <S.LoginLabel htmlFor="account-password">
                {t('account.password')}
              </S.LoginLabel>
              <S.LoginInput
                id="account-password"
                aria-label={t('account.password')}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={t('account.password')}
              />
            </S.LoginField>
            <S.SignInButton type="submit">{t('account.signIn')}</S.SignInButton>
            {authError && <S.AccountError>{authError}</S.AccountError>}
          </S.LoginForm>
        ) : (
          <S.AccountControls>
            <S.AccountButton
              type="button"
              onClick={() => setAccountView('catalogue')}
              isSelected={accountView === 'catalogue'}
            >
              {t('account.catalogue')}
            </S.AccountButton>
            <S.AccountButton
              type="button"
              onClick={() => setAccountView('favourites')}
              isSelected={accountView === 'favourites'}
            >
              {t('account.favourites')}
            </S.AccountButton>
            <S.AccountButton
              type="button"
              onClick={() => setAccountView('orders')}
              isSelected={accountView === 'orders'}
              disabled={!isRestaurant}
            >
              {t('account.orderHistory')}
            </S.AccountButton>
            <S.AccountButton type="button" onClick={handleLogout}>
              {t('account.signOut')}
            </S.AccountButton>
          </S.AccountControls>
        )}
      </S.AccountBar>
      <S.TwoColumnGrid $isSingleColumn={isOrderHistoryView}>
        {!isOrderHistoryView && (
          <S.Side>
            <Filter />
          </S.Side>
        )}
        <S.Main>
          {!isOrderHistoryView && (
            <>
              <S.MainHeader>
                <p>{t('products.found', { count: visibleProducts.length })}</p>
              </S.MainHeader>
              <S.SearchBar>
                <S.SearchLabel htmlFor="product-search">
                  {t('products.search')}
                </S.SearchLabel>
                <S.SearchInput
                  id="product-search"
                  type="search"
                  value={searchTerm}
                  onChange={handleSearchTermChange}
                  placeholder={t('products.searchPlaceholder')}
                />
              </S.SearchBar>
            </>
          )}
          {isOrderHistoryView ? (
            <S.OrderHistory>
              {isAccountLoading ? (
                <S.EmptyState>{t('account.loading')}</S.EmptyState>
              ) : selectedOrder ? (
                <S.OrderDetail>
                  <S.OrderDetailHeader>
                    <S.OrderBackButton
                      type="button"
                      onClick={() => setSelectedOrder(null)}
                    >
                      {t('account.backToOrders')}
                    </S.OrderBackButton>
                    <S.OrderDetailTitle>
                      {t('account.orderDetails', { id: selectedOrder.id })}
                    </S.OrderDetailTitle>
                    <S.OrderDetailMeta>
                      <span>{selectedOrder.restaurant_name}</span>
                      <span>{selectedOrder.status}</span>
                      <span>
                        {new Date(
                          selectedOrder.date_ordered
                        ).toLocaleDateString()}
                      </span>
                    </S.OrderDetailMeta>
                    {selectedOrder.note ? (
                      <S.OrderNote>{selectedOrder.note}</S.OrderNote>
                    ) : null}
                  </S.OrderDetailHeader>
                  <S.OrderDetailGroups>
                    {groupedSelectedOrderItems.map((group) => (
                      <S.OrderDetailGroup key={group.category}>
                        <S.OrderDetailCategory>
                          {group.category}
                        </S.OrderDetailCategory>
                        {group.items.map(({ index, item }) => (
                          <S.OrderDetailItem key={item.id}>
                            <S.OrderDetailItemName>
                              {index}. {getOrderItemName(item)}
                            </S.OrderDetailItemName>
                            <S.OrderDetailItemMeta>
                              <span>
                                {item.quantity} {item.unit_snapshot}
                              </span>
                              {item.package_spec_snapshot ? (
                                <span>{item.package_spec_snapshot}</span>
                              ) : null}
                              {item.price_snapshot ? (
                                <span>{item.price_snapshot}</span>
                              ) : null}
                            </S.OrderDetailItemMeta>
                          </S.OrderDetailItem>
                        ))}
                      </S.OrderDetailGroup>
                    ))}
                  </S.OrderDetailGroups>
                  <S.ReorderButton
                    type="button"
                    onClick={() => handleReorder(selectedOrder)}
                  >
                    {t('account.reorder')}
                  </S.ReorderButton>
                </S.OrderDetail>
              ) : orders.length ? (
                orders.map((order) => (
                  <S.OrderHistoryItem key={order.id}>
                    <strong>#{order.id}</strong>
                    <span>{order.restaurant_name}</span>
                    <span>{order.status}</span>
                    <span>
                      {new Date(order.date_ordered).toLocaleDateString()}
                    </span>
                    <S.ViewDetailsButton
                      type="button"
                      onClick={() => setSelectedOrder(order)}
                    >
                      {t('account.viewDetails')}
                    </S.ViewDetailsButton>
                    <S.ReorderButton
                      type="button"
                      onClick={() => handleReorder(order)}
                    >
                      {t('account.reorder')}
                    </S.ReorderButton>
                  </S.OrderHistoryItem>
                ))
              ) : (
                <S.EmptyState>
                  {isRestaurant
                    ? t('account.noOrders')
                    : t('account.restaurantOnly')}
                </S.EmptyState>
              )}
            </S.OrderHistory>
          ) : (
            <>
              {accountView === 'favourites' && !visibleProducts.length ? (
                <S.EmptyState>{t('account.noFavourites')}</S.EmptyState>
              ) : null}
              <Products
                products={visibleProducts}
                onToggleFavourite={
                  isAuthenticated ? handleToggleFavourite : undefined
                }
              />
            </>
          )}
        </S.Main>
      </S.TwoColumnGrid>
      <Cart onOrderSuccess={handleOrderSuccess} />
    </S.Container>
  );
}

export default App;
